import clsx from "clsx";
import React from "react";

import { useStrings } from "../../context/ConfiguratorStringsContext.ts";
import type { ValidationStatus, RuleResult } from "../../types/validation";
import { VALIDATION_STATUS } from "../../types/validation";
import {
	Change,
	CheckmarkCircle,
	ExclamationMarkTriangle,
} from "../common/icons";

import styles from "./ComponentCard.module.css";

interface ValidationBadgesProps {
	validationStatus: ValidationStatus;
	validationErrors?: RuleResult[];
}

const ValidationBadges: React.FC<ValidationBadgesProps> = ({
	validationStatus,
	validationErrors,
}) => {
	const strings = useStrings();

	if (!validationStatus) return null;

	if (!validationErrors || validationErrors.length === 0) {
		return (
			<span
				className={clsx(styles.validationBadge, styles.success)}
				role="status"
			>
				<CheckmarkCircle aria-hidden="true" />
				<span className={styles.validationBadgeText}>
					{strings.product.configBundles.compatibility.compatible}
				</span>
			</span>
		);
	}

	return (
		<ul className={styles.validationBadgeList} role="list">
			{validationErrors.map((rule, i) => {
				const isError = rule.severity === VALIDATION_STATUS.ERROR;
				const isWarning = rule.severity === VALIDATION_STATUS.WARNING;
				const message =
					rule.shortMessage ||
					strings.product.configBundles.compatibility.notCompatible;

				return (
					<li
						key={`${rule.severity}-${rule.shortMessage ?? i}`}
						className={clsx(styles.validationBadge, {
							[styles.error]: isError,
							[styles.warning]: isWarning,
							[styles.success]: !isError && !isWarning,
						})}
					>
						{isError ? (
							<Change aria-hidden="true" />
						) : isWarning ? (
							<ExclamationMarkTriangle aria-hidden="true" />
						) : (
							<CheckmarkCircle aria-hidden="true" />
						)}
						<span className={styles.validationBadgeText} title={message}>
							{message}
						</span>
					</li>
				);
			})}
		</ul>
	);
};

export default ValidationBadges;
